import { lautGroupLabel, type LautGroup } from "@calega/content";
import { GROSSKLEIN_RULES, readingSnapshotSchema, READING_UNITS } from "@calega/content";
export interface RunAnswerDetail {
  itemKey: string;
  answer: unknown;
  correct: boolean | null;
  createdAt?: string;
  dataOrigin?: "native_v2" | "legacy_v1";
  sourceItemIndex?: number | null;
  legacyPrompt?: string | null;
  legacyGivenAnswer?: unknown;
  legacyExpectedAnswer?: unknown;
}

export interface DocumentableRunDetail {
  exerciseType: string;
  settings: unknown;
  answers: RunAnswerDetail[];
  itemLogs?: Array<{ itemKey: string; derivedMetrics?: unknown }>;
  contentPage?: {
    publishedVersion?: { exerciseConfig?: unknown } | null;
  } | null;
}

export interface DocumentationToken {
  text: string;
  after: string;
  tone: "correct" | "wrong" | "neutral";
}

export interface DocumentationWallCell {
  key: string;
  value: number | null;
  expected: number;
  tone: "fixed" | "correct" | "wrong";
}

export interface DocumentationWallAttempt {
  label: string;
  correct: boolean;
  rows: DocumentationWallCell[][];
}

export interface DocumentationItem {
  key: string;
  correct: boolean | null;
  correctUnits: number;
  wrongUnits: number;
  prompt: string;
  response?: string;
  expected?: string;
  notes: string[];
  status?: "uncertain";
  tokens?: DocumentationToken[];
  wall?: { attempts: DocumentationWallAttempt[] };
}

export interface RunDocumentation {
  settings: Array<{ label: string; value: string }>;
  items: DocumentationItem[];
  correctCount: number;
  uncertainCount: number;
  wrongCount: number;
}

export interface RunMarkdownMeta {
  title: string;
  learnerName: string;
  exerciseType: string;
  startedAt: string;
  durationSeconds: number | null;
  status?: string;
  abortReason?: "INACTIVITY" | "TIMED_NO_ANSWER" | null;
  score: number | null;
  maxScore: number | null;
  wrongAnswerCount: number;
}

type JsonObject = Record<string, unknown>;

const settingLabels: Record<string, string> = {
  position: "Wortposition", soundCounts: "Anzahl gesprochener Laute",
  trainingMode: "Trainingsart",
  rows: "Reihen",
  mode: "Modus",
  modus: "Modus",
  count: "Anzahl Aufgaben",
  anzahl: "Anzahl Aufgaben",
  wordCount: "Anzahl Wörter",
  durationSeconds: "Dauer",
  dauerSek: "Dauer",
  displayDurationMs: "Anzeigedauer",
  syllableColors: "Silbenfarben",
  rules: "Regeln",
  categories: "Kategorien",
  includeNominalizations: "Nominalisierungen",
  syllableCounts: "Silbenanzahl",
  materialBankId: "Material",
  groupIds: "Materialgruppen",
  vowelMarking: "Vokale markieren",
  graphemeHighlight: "Grapheme hervorheben",
  tenses: "Zeitformen",
  difficulty: "Schwierigkeit",
  range: "Zahlenraum",
  zahlenraum: "Zahlenraum",
  arrangement: "Anordnung",
  anordnung: "Anordnung",
  sizes: "Objektgrößen",
  groessen: "Objektgrößen",
  operations: "Rechenarten",
  phase: "Lernphase",
  operation: "Rechenart",
  categoryCount: "Anzahl Kategorien",
  objectsPerCategory: "Objekte je Kategorie",
  places: "Stellen",
  decimals: "Dezimalzahlen",
  multiplicationProfile: "Multiplikationsprofil",
  multiplicationMethod: "Multiplikationsverfahren",
  divisionProfile: "Divisionsprofil",
  immediateFeedback: "Soforthilfe beim Eingeben",
  roles: "Satzglieder",
  randomOrder: "Zufällige Reihenfolge",
  doubleConfirmation: "Doppelte Bestätigung",
  confirmationDurationSeconds: "Bestätigungsdauer",
};

const valueLabels: Record<string, string> = {
  time: "Zeit",
  count: "Anzahl",
  anzahl: "Anzahl",
  addition: "Addition",
  subtraction: "Subtraktion",
  multiplication: "Multiplikation",
  division: "Division",
  mixed: "Gemischt",
  leicht: "Leicht",
  mittel: "Mittel",
  schwer: "Schwer",
  geordnet: "Geordnet",
  gestreut: "Gestreut",
  einheitlich: "Einheitlich",
  variierend: "Variierend",
  praesens: "Präsens",
  praeteritum: "Präteritum",
  perfekt: "Perfekt",
  plusquamperfekt: "Plusquamperfekt",
  futur1: "Futur I",
  futur2: "Futur II",
  left: "Links",
  equal: "Gleich",
  right: "Rechts",
  correct: "richtig",
  uncertain: "unsicher",
  incorrect: "falsch",
  ones: "Einerstelle",
  tens: "Zehnerstelle",
  hundreds: "Hunderterstelle",
  thousands: "Tausenderstelle",
  "ten-thousands": "Zehntausenderstelle",
  "hundred-thousands": "Hunderttausenderstelle",
  move_tens: "Zehner bewegen",
  observe_material: "Material anschauen",
  mental_short: "Kurz im Kopf",
  silben: "Silben",
  pseudowoerter: "Pseudowörter",
  "grundwortschatz-hessen": "Grundwortschatz Hessen",
  einfach: "Einfache Silben",
  konsonantenverbindungen: "Konsonantenverbindungen",
  "1-silbig": "1-silbig",
  "2-silbig": "2-silbig",
  "3-silbig": "3-silbig",
  "4-plus-silbig": "4+-silbig",
};

function object(value: unknown): JsonObject | null {
  return value !== null && typeof value === "object" && !Array.isArray(value) ? value as JsonObject : null;
}

function objects(value: unknown): JsonObject[] {
  return Array.isArray(value) ? value.map(object).filter((entry): entry is JsonObject => Boolean(entry)) : [];
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];
}

function numbers(value: unknown): number[] {
  return Array.isArray(value) ? value.filter((entry): entry is number => typeof entry === "number") : [];
}

function formatDuration(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes ? `${minutes} min ` : ""}${rest} s`;
}

function displayStoredValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "Keine Angabe";
  if (typeof value === "boolean") return value ? "Ja" : "Nein";
  if (typeof value === "string") return valueLabels[value] ?? value;
  if (typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.map(displayStoredValue).join(", ");
  const entry = object(value);
  if (entry) return Object.entries(entry).map(([key, nested]) => `${key}: ${displayStoredValue(nested)}`).join(", ");
  return String(value);
}

// Task labels and answer values are scalar text, never arbitrary diagnostic JSON.
function display(value: unknown): string {
  if (Array.isArray(value)) return value.map(display).join(", ");
  return value !== null && typeof value === "object" ? "Nicht vollständig gespeichert" : displayStoredValue(value);
}

export function formatSettingValue(key: string, value: unknown) {
  if (key === "rules" && Array.isArray(value)) return value.map((id) => GROSSKLEIN_RULES.find((rule) => rule.id === id)?.label ?? display(id)).join(", ");
  if (key === "trainingMode" && value === "core") return "Kernaufgaben";
  if (key === "trainingMode" && value === "rows") return "Reihen üben";
  if ((key === "durationSeconds" || key === "dauerSek" || key === "confirmationDurationSeconds") && typeof value === "number") return formatDuration(value);
  if (key === "displayDurationMs" && typeof value === "number") return `${value} ms`;
  if (key === "places" && typeof value === "number") return `${value}-stellig`;
  if (key === "multiplicationMethod" && value === "standard") return "Kompakt mit Übertrag";
  if (key === "multiplicationMethod" && value === "place-value") return "Stellenweise Teilprodukte";
  if (key === "multiplicationProfile" && typeof value === "string") return `${value.replace("x", " × ")} Stellen`;
  if (key === "divisionProfile" && typeof value === "string") return `${value.replace(":", " : ")} Stellen`;
  return displayStoredValue(value);
}

export function runSettingLabel(key: string) {
  return settingLabels[key];
}

export function formatRunDuration(seconds: number) {
  const hours = Math.floor(seconds / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  const rest = seconds % 60;
  return [hours ? `${hours} Std.` : "", minutes ? `${minutes} Min.` : "", `${rest} Sek.`].filter(Boolean).join(" ");
}

export function runDurationLabel(run: { durationSeconds: number | null; abortReason: "INACTIVITY" | "TIMED_NO_ANSWER" | null }) {
  if (run.durationSeconds === null) return "Dauer noch offen";
  return `${formatRunDuration(run.durationSeconds)}${run.abortReason ? " bis zur letzten Eingabe" : ""}`;
}

export function runAbortLabel(reason: "INACTIVITY" | "TIMED_NO_ANSWER" | null) {
  return reason === "INACTIVITY" ? "Automatisch abgebrochen nach 15 Minuten Inaktivität"
    : reason === "TIMED_NO_ANSWER" ? "Abgebrochen – Zeitende ohne Antwort" : null;
}

function latestAnswers(answers: RunAnswerDetail[]) {
  const latest = new Map<string, RunAnswerDetail>();
  for (const answer of answers) latest.set(answer.itemKey, answer);
  return [...latest.values()];
}

function findById(value: unknown, id: string) {
  return objects(value).find((entry) => entry.id === id) ?? null;
}

function operationSymbol(value: unknown) {
  return value === "addition" ? "+" : value === "subtraction" ? "−" : value === "multiplication" ? "×" : value === "division" ? "÷" : display(value);
}

function sentenceText(tokens: JsonObject[], suffix = "") {
  return tokens.map((token, index) => `${display(token.surface ?? token.text)}${typeof token.after === "string" ? token.after : index + 1 < tokens.length ? " " : ""}`).join("") + suffix;
}

function splitWord(surface: string, positions: number[]) {
  const splits = new Set(positions);
  return Array.from(surface).map((letter, index) => `${splits.has(index) ? "|" : ""}${letter}`).join("");
}

function genericItem(answer: RunAnswerDetail, prompt: string, response?: string, expected?: string, notes: string[] = []): DocumentationItem {
  return { key: answer.itemKey, correct: answer.correct, correctUnits: answer.correct === true ? 1 : 0, wrongUnits: answer.correct === false ? 1 : 0, prompt, response, expected, notes };
}

function legacyAnswerItem(answer: RunAnswerDetail) {
  const prompt = typeof answer.legacyPrompt === "string" && answer.legacyPrompt.trim()
    ? answer.legacyPrompt
    : "V1-Aufgabe ohne gespeicherte Aufgabenbeschreibung";
  const notes = answer.sourceItemIndex === null
    ? ["Der ursprüngliche Aufgabenindex wurde in V1 nicht gespeichert."]
    : [];
  return genericItem(
    answer,
    prompt,
    displayStoredValue(answer.legacyGivenAnswer),
    displayStoredValue(answer.legacyExpectedAnswer),
    notes,
  );
}

interface LegacyWordartenTokenAnswer {
  answer: RunAnswerDetail;
  sentence: string;
  word: string;
  position: number | null;
  chosenPos: string | null;
  expectedPos: string | null;
}

function legacyWordartenTokenAnswer(answer: RunAnswerDetail): LegacyWordartenTokenAnswer | null {
  const value = object(answer.answer);
  if (value?.type !== "legacy-wortarten-token" || typeof value.sentence !== "string" || typeof value.word !== "string") return null;
  return {
    answer,
    sentence: value.sentence,
    word: value.word,
    position: typeof value.position === "number" && Number.isInteger(value.position) ? value.position : null,
    chosenPos: typeof value.chosenPos === "string" ? value.chosenPos : null,
    expectedPos: typeof value.expectedPos === "string" ? value.expectedPos : null,
  };
}

function legacyWordartenItems(answers: RunAnswerDetail[]): DocumentationItem[] | null {
  const tokens = answers.map(legacyWordartenTokenAnswer);
  if (!tokens.length || tokens.some((token) => token === null)) return null;
  const groups = new Map<string, LegacyWordartenTokenAnswer[]>();
  for (const token of tokens as LegacyWordartenTokenAnswer[]) {
    const key = `${token.answer.sourceItemIndex ?? "index-na"}:${token.sentence}`;
    groups.set(key, [...(groups.get(key) ?? []), token]);
  }
  return [...groups.values()]
    .sort((left, right) => (left[0]!.answer.sourceItemIndex ?? Number.MAX_SAFE_INTEGER) - (right[0]!.answer.sourceItemIndex ?? Number.MAX_SAFE_INTEGER))
    .map((group) => {
      const ordered = [...group].sort((left, right) => (left.position ?? Number.MAX_SAFE_INTEGER) - (right.position ?? Number.MAX_SAFE_INTEGER));
      let cursor = 0;
      const starts = ordered.map((token) => {
        const start = token.sentence.indexOf(token.word, cursor);
        if (start >= 0) cursor = start + token.word.length;
        return start;
      });
      const documentedTokens = ordered.map((token, index): DocumentationToken => {
        const start = starts[index] ?? -1;
        const end = start >= 0 ? start + token.word.length : -1;
        const nextStart = starts[index + 1] ?? token.sentence.length;
        return {
          text: token.word,
          after: end >= 0 && nextStart >= end ? token.sentence.slice(end, nextStart) : index + 1 < ordered.length ? " " : "",
          tone: token.answer.correct === true ? "correct" : "wrong",
        };
      });
      const wrong = ordered.filter(({ answer }) => answer.correct !== true);
      return {
        key: ordered[0]!.answer.itemKey,
        correct: wrong.length === 0,
        correctUnits: ordered.length - wrong.length,
        wrongUnits: wrong.length,
        prompt: ordered[0]!.sentence,
        notes: wrong.map((token) => `${token.word}: ${display(token.chosenPos)} (richtig: ${display(token.expectedPos)})`),
        tokens: documentedTokens,
      };
    });
}

function grosskleinItem(answer: RunAnswerDetail, generated: JsonObject): DocumentationItem | null {
  const sentence = findById(generated.sentences, answer.itemKey);
  if (!sentence) return null;
  const tokens = objects(sentence.tokens);
  const selected = new Set(strings(object(answer.answer)?.selectedTokenIds));
  const notes: string[] = [];
  const documentedTokens = tokens.map((token, index): DocumentationToken => {
    const tokenId = String(token.id ?? "");
    const expected = token.expectedCapitalized === true;
    const chosen = selected.has(tokenId);
    const surface = display(token.surface);
    if (expected && !chosen) notes.push(`Übersehen: ${surface.replace(/^./, (letter) => letter.toLocaleUpperCase("de-DE"))}`);
    if (!expected && chosen) notes.push(`Zu viel markiert: ${surface}`);
    return {
      text: surface,
      after: typeof token.after === "string" ? token.after : index + 1 < tokens.length ? " " : "",
      tone: expected || chosen ? expected === chosen ? "correct" : "wrong" : "neutral",
    };
  });
  const suffix = typeof sentence.suffix === "string" ? sentence.suffix : "";
  if (suffix) documentedTokens.push({ text: suffix, after: "", tone: "neutral" });
  const correctUnits = tokens.filter((token) => selected.has(String(token.id ?? "")) === (token.expectedCapitalized === true)).length;
  return {
    key: answer.itemKey,
    correct: answer.correct,
    correctUnits,
    wrongUnits: tokens.length - correctUnits,
    prompt: sentenceText(tokens, suffix),
    notes,
    tokens: documentedTokens,
  };
}

function wortartenItem(answer: RunAnswerDetail, generated: JsonObject): DocumentationItem | null {
  const sentence = findById(generated.sentences, answer.itemKey);
  if (!sentence) return null;
  const tokens = objects(sentence.tokens);
  const assignments = new Map(objects(object(answer.answer)?.assignments).map((entry) => [String(entry.tokenId ?? ""), entry.chosenPos]));
  const notes: string[] = [];
  const documentedTokens = tokens.map((token, index): DocumentationToken => {
    const chosen = assignments.get(String(token.id ?? ""));
    const expected = token.expectedPos;
    const correct = chosen === expected;
    if (!correct) notes.push(`${display(token.surface)}: ${display(chosen)} (richtig: ${display(expected)})`);
    return {
      text: display(token.surface),
      after: typeof token.after === "string" ? token.after : index + 1 < tokens.length ? " " : "",
      tone: correct ? "correct" : "wrong",
    };
  });
  const suffix = typeof sentence.suffix === "string" ? sentence.suffix : "";
  if (suffix) documentedTokens.push({ text: suffix, after: "", tone: "neutral" });
  const correctUnits = tokens.filter((token) => assignments.get(String(token.id ?? "")) === token.expectedPos).length;
  return { key: answer.itemKey, correct: answer.correct, correctUnits, wrongUnits: tokens.length - correctUnits, prompt: sentenceText(tokens, suffix), notes, tokens: documentedTokens };
}

function satzgliederItem(answer: RunAnswerDetail, generated: JsonObject): DocumentationItem | null {
  const sentence = findById(generated.sentences, answer.itemKey);
  if (!sentence) return null;
  const chunks = objects(sentence.chunks);
  const assignments = new Map(objects(object(answer.answer)?.assignments).map((entry) => [String(entry.chunkId ?? ""), entry.chosenRole]));
  const notes: string[] = [];
  const tokens = chunks.map((chunk, index): DocumentationToken => {
    const chosen = assignments.get(String(chunk.id ?? ""));
    const expected = chunk.expectedRole;
    const correct = chosen === expected;
    if (!correct) notes.push(`${display(chunk.text)}: ${display(chosen)} (richtig: ${display(expected)})`);
    return { text: display(chunk.text), after: index + 1 < chunks.length ? " " : "", tone: correct ? "correct" : "wrong" };
  });
  const suffix = typeof sentence.suffix === "string" ? sentence.suffix : "";
  if (suffix) tokens.push({ text: suffix, after: "", tone: "neutral" });
  const correctUnits = chunks.filter((chunk) => assignments.get(String(chunk.id ?? "")) === chunk.expectedRole).length;
  return { key: answer.itemKey, correct: answer.correct, correctUnits, wrongUnits: chunks.length - correctUnits, prompt: chunks.map((chunk) => display(chunk.text)).join(" ") + suffix, notes, tokens };
}

function numericValue(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function sumExpression(values: number[]) {
  return `${values.map((value) => value.toLocaleString("de-DE")).join(" + ")} = ${values.reduce((sum, value) => sum + value, 0).toLocaleString("de-DE")}`;
}

function zahlenZerlegenSolutions(available: number[], target: number) {
  const solutions = new Map<string, number[]>();
  for (let mask = 0; mask < 2 ** available.length; mask += 1) {
    const selected = available.filter((_, index) => (mask & (1 << index)) !== 0);
    if (selected.length < 3 || selected.reduce((sum, value) => sum + value, 0) !== target) continue;
    const normalized = [...selected].sort((left, right) => left - right);
    solutions.set(normalized.join("|"), normalized);
  }
  return [...solutions.values()].sort((left, right) => left.length - right.length || left.join("|").localeCompare(right.join("|"), "de"));
}

function zahlenZerlegenItem(answer: RunAnswerDetail, value: JsonObject) {
  const available = numbers(value.numbers);
  const selected = numbers(value.selectedNumbers);
  const target = numericValue(value.target);
  if (target === null || !available.length) return genericItem(answer, `${display(value.numbers)} → ${display(value.target)}`, display(value.selectedNumbers), display(value.target));
  const solutions = zahlenZerlegenSolutions(available, target);
  return genericItem(
    answer,
    `Zielzahl ${target.toLocaleString("de-DE")} · Auswahlzahlen: ${available.map((entry) => entry.toLocaleString("de-DE")).join(", ")}`,
    selected.length ? sumExpression(selected) : "Keine Zahlen ausgewählt",
    solutions.length ? solutions.map(sumExpression).join("; ") : `Keine gültige Kombination gespeichert`,
  );
}

function rechenmauerAttempt(rows: number[][], values: JsonObject, label: string): DocumentationWallAttempt {
  const documentedRows = rows.map((row, rowIndex) => row.map((expected, cellIndex): DocumentationWallCell => {
    const key = `r${rowIndex}c${cellIndex}`;
    if (rowIndex === 0) return { key, value: expected, expected, tone: "fixed" };
    const entered = numericValue(values[key]);
    return { key, value: entered, expected, tone: entered === expected ? "correct" : "wrong" };
  })).reverse();
  return {
    label,
    correct: documentedRows.every((row) => row.every((cell) => cell.tone !== "wrong")),
    rows: documentedRows,
  };
}

function rechenmauerValuesFromAnalysis(value: unknown) {
  return Object.fromEntries(objects(object(value)?.cells).map((cell) => [String(cell.cellKey ?? ""), numericValue(cell.entered)]));
}

function rechenmauerErrorNotes(attempt: DocumentationWallAttempt, rowCount: number) {
  return attempt.rows.flatMap((row) => row.flatMap((cell) => {
    if (cell.tone !== "wrong") return [];
    const match = /^r(\d+)c(\d+)$/.exec(cell.key);
    const rowIndex = Number(match?.[1] ?? 0);
    const cellIndex = Number(match?.[2] ?? 0);
    const position = rowIndex === rowCount - 1 ? "Spitze" : `${rowIndex + 1}. Reihe von unten, Stein ${cellIndex + 1}`;
    return [`${attempt.label}, ${position}: ${cell.value ?? "keine Eingabe"} (richtig: ${cell.expected})`];
  }));
}

function rechenmauerItem(answer: RunAnswerDetail, generated: JsonObject, detail: DocumentableRunDetail): DocumentationItem | null {
  const wall = findById(generated.walls, answer.itemKey);
  const rows = Array.isArray(wall?.rows) ? wall.rows.map(numbers).filter((row) => row.length > 0) : [];
  if (!rows.length) return null;
  const log = detail.itemLogs?.find((entry) => entry.itemKey === answer.itemKey);
  const history = objects(object(log?.derivedMetrics)?.attemptHistory);
  const currentValues = object(object(answer.answer)?.values) ?? {};
  const attempts = history.length
    ? history.map((entry, index) => {
        const submitAttempt = numericValue(entry.submitAttempt);
        return rechenmauerAttempt(rows, rechenmauerValuesFromAnalysis(entry.analysis), `Versuch ${submitAttempt ?? index + 1}`);
      })
    : [rechenmauerAttempt(rows, currentValues, "Abgabe")];
  const notes = attempts.flatMap((attempt) => rechenmauerErrorNotes(attempt, rows.length));
  return {
    ...genericItem(answer, `Basis ${rows[0]!.join(", ")} · Spitze ${rows.at(-1)![0]}`, undefined, undefined, notes),
    wall: { attempts },
  };
}

function writtenFieldColumn(key: string) {
  return Number(key.match(/-(\d+)$/)?.[1] ?? 0);
}

function writtenFieldValue(values: JsonObject, key: string) {
  const value = values[key];
  return typeof value === "string" || typeof value === "number" ? String(value).trim() : "";
}

function writtenFieldMatches(expected: string, actual: string) {
  const normalizedExpected = expected.replace(/\s+/g, "");
  const normalizedActual = actual.replace(/\s+/g, "");
  if (!normalizedExpected) return !normalizedActual || normalizedActual === "0";
  return /^\d+$/.test(normalizedActual)
    ? String(Number(normalizedActual)) === String(Number(normalizedExpected))
    : normalizedActual === normalizedExpected;
}

function writtenCompactRow(values: JsonObject, expected: JsonObject, keys: string[]) {
  const occupiedColumns = keys
    .filter((key) => writtenFieldValue(expected, key) || writtenFieldValue(values, key))
    .map(writtenFieldColumn);
  if (!occupiedColumns.length) return "keine Eingabe";
  const first = Math.min(...occupiedColumns);
  const last = Math.max(...occupiedColumns);
  const keyByColumn = new Map(keys.map((key) => [writtenFieldColumn(key), key]));
  return Array.from({ length: last - first + 1 }, (_, offset) => {
    const key = keyByColumn.get(first + offset);
    return key ? writtenFieldValue(values, key) || "□" : "□";
  }).join("");
}

function writtenAlignedRow(values: JsonObject, keys: string[], width: number) {
  const keyByColumn = new Map(keys.map((key) => [writtenFieldColumn(key), key]));
  return Array.from({ length: width }, (_, column) => {
    const key = keyByColumn.get(column);
    return key ? writtenFieldValue(values, key) || "·" : "·";
  }).join(" ");
}

function writtenArithmeticSummary(values: JsonObject, expected: JsonObject) {
  const keys = Object.keys(expected);
  const width = Math.max(1, ...keys.map(writtenFieldColumn)) + 1;
  const lines: string[] = [];
  const simpleRow = (pattern: RegExp, label: string, aligned = false) => {
    const rowKeys = keys.filter((key) => pattern.test(key)).sort((left, right) => writtenFieldColumn(left) - writtenFieldColumn(right));
    if (!rowKeys.length || !rowKeys.some((key) => writtenFieldValue(expected, key) || writtenFieldValue(values, key))) return;
    lines.push(`${label}: ${aligned ? writtenAlignedRow(values, rowKeys, width) : writtenCompactRow(values, expected, rowKeys)}`);
  };
  const indexedRows = (pattern: RegExp, label: (index: number) => string) => {
    const indices = [...new Set(keys.flatMap((key) => {
      const match = pattern.exec(key);
      return match ? [Number(match[1])] : [];
    }))].sort((left, right) => left - right);
    for (const index of indices) {
      const rowKeys = keys.filter((key) => pattern.test(key) && Number(pattern.exec(key)?.[1]) === index);
      lines.push(`${label(index)}: ${writtenAlignedRow(values, rowKeys, width)}`);
    }
  };

  simpleRow(/^result-\d+$/, "Ergebnis");
  simpleRow(/^carry-\d+$/, "Überträge", true);
  simpleRow(/^helper-\d+$/, "Hilfszahlen", true);
  simpleRow(/^repay-\d+$/, "Entbündelungszahlen", true);
  simpleRow(/^addition-carry-\d+$/, "Überträge der Teilprodukte", true);
  simpleRow(/^quot-\d+$/, "Quotient");
  indexedRows(/^partial-(\d+)-\d+$/, (index) => `Teilprodukt ${index + 1}`);
  indexedRows(/^div-product-(\d+)-\d+$/, (index) => `Divisionsschritt ${index + 1}, abgezogene Zahl`);
  indexedRows(/^div-next-(\d+)-\d+$/, (index) => `Divisionsschritt ${index + 1}, Zwischenergebnis`);
  return lines.join("; ") || "keine Eingabe";
}

function writtenFieldLabel(key: string, width: number) {
  const column = writtenFieldColumn(key);
  const place = ["Einerstelle", "Zehnerstelle", "Hunderterstelle", "Tausenderstelle", "Zehntausenderstelle", "Hunderttausenderstelle"][width - column - 1] ?? `Spalte ${column + 1}`;
  const partial = /^partial-(\d+)-/.exec(key);
  if (partial) return `Teilprodukt ${Number(partial[1]) + 1}, ${place}`;
  const divisionProduct = /^div-product-(\d+)-/.exec(key);
  if (divisionProduct) return `Divisionsschritt ${Number(divisionProduct[1]) + 1}, abgezogene Zahl, ${place}`;
  const divisionNext = /^div-next-(\d+)-/.exec(key);
  if (divisionNext) return `Divisionsschritt ${Number(divisionNext[1]) + 1}, Zwischenergebnis, ${place}`;
  if (key.startsWith("result-")) return `Ergebnis, ${place}`;
  if (key.startsWith("carry-")) return `Übertrag, ${place}`;
  if (key.startsWith("addition-carry-")) return `Übertrag der Teilprodukte, ${place}`;
  if (key.startsWith("helper-")) return `Hilfszahl, ${place}`;
  if (key.startsWith("repay-")) return `Entbündelungszahl, ${place}`;
  if (key.startsWith("quot-")) return `Quotient, Ziffer ${column + 1}`;
  return `Eingabefeld, ${place}`;
}

function writtenArithmeticItem(answer: RunAnswerDetail, value: JsonObject) {
  const entered = object(value.values) ?? {};
  const expected = object(value.expectedFields) ?? {};
  const keys = Object.keys(expected);
  const width = Math.max(1, ...keys.map(writtenFieldColumn)) + 1;
  const skipped = value.skipped === true;
  const notes = skipped
    ? ["Aufgabe wurde übersprungen."]
    : keys.flatMap((key) => {
        const enteredValue = writtenFieldValue(entered, key);
        const expectedValue = writtenFieldValue(expected, key);
        return writtenFieldMatches(expectedValue, enteredValue)
          ? []
          : [`${writtenFieldLabel(key, width)}: ${enteredValue || "keine Eingabe"} (richtig: ${expectedValue || "leer"})`];
      });
  if (typeof value.submitAttempts === "number" && value.submitAttempts > 1) notes.unshift(`Abgabeversuche: ${value.submitAttempts}`);
  return genericItem(
    answer,
    display(value.prompt ?? answer.itemKey),
    skipped ? "Übersprungen" : writtenArithmeticSummary(entered, expected),
    writtenArithmeticSummary(expected, expected),
    notes,
  );
}

function answerItem(exerciseType: string, answer: RunAnswerDetail, detail: DocumentableRunDetail, learner = false): DocumentationItem {
  const value = object(answer.answer) ?? {};
  const generated = object(detail.settings) ?? {};
  const legacy = () => learner
    ? genericItem(answer, answer.legacyPrompt || "V1-Aufgabe ohne gespeicherte Aufgabenbeschreibung", display(answer.legacyGivenAnswer), display(answer.legacyExpectedAnswer))
    : legacyAnswerItem(answer);
  if (answer.dataOrigin === "legacy_v1" && Object.keys(value).length === 0) return legacy();
  const specialized = exerciseType === "grossklein"
    ? grosskleinItem(answer, generated)
    : exerciseType === "wortarten"
      ? wortartenItem(answer, generated)
      : exerciseType === "satzglieder"
        ? satzgliederItem(answer, generated)
        : exerciseType === "rechenmauer"
          ? rechenmauerItem(answer, generated, detail)
        : null;
  if (specialized) {
    if (learner && ["grossklein", "wortarten", "satzglieder"].includes(exerciseType)) {
      const sentence = findById(generated.sentences, answer.itemKey);
      const assignments = objects(value.assignments);
      const parts = objects(exerciseType === "satzglieder" ? sentence?.chunks : sentence?.tokens);
      const selected = new Set(strings(value.selectedTokenIds));
      specialized.response = parts.map((part) => exerciseType === "grossklein"
        ? `${display(part.surface)}: ${selected.has(String(part.id)) ? "groß" : "klein"}`
        : `${display(part.surface ?? part.text)}: ${display(assignments.find((entry) => (entry.tokenId ?? entry.chunkId) === part.id)?.[exerciseType === "wortarten" ? "chosenPos" : "chosenRole"])}`).join("; ");
      specialized.expected = parts.map((part) => `${display(part.surface ?? part.text)}: ${exerciseType === "grossklein" ? part.expectedCapitalized ? "groß" : "klein" : display(part.expectedPos ?? part.expectedRole)}`).join("; ");
    }
    return specialized;
  }

  if (learner && value.type === "legacy-wortarten-token") return genericItem(answer, display(value.sentence), `${display(value.word)}: ${display(value.chosenPos)}`, `${display(value.word)}: ${display(value.expectedPos)}`);
  if (learner && exerciseType === "flashcards") return genericItem({ ...answer, correct: null }, display(value.front), ["Nochmal", "Schwer", "Gut", "Einfach"][Number(value.rating) - 1] ?? "Nicht gespeichert", display(value.back));
  if (learner && exerciseType === "einmaleins-trainer") {
    const item = findById(generated.items, answer.itemKey);
    return genericItem(answer, item ? `${display(item.left)} × ${display(item.right)}` : "Aufgabenstellung nicht vollständig gespeichert", value.skipped ? "Übersprungen" : display(value.value), display(value.expected ?? item?.result));
  }

  if (exerciseType === "quiz") {
    const config = object(detail.contentPage?.publishedVersion?.exerciseConfig);
    const question = findById(config?.questions, answer.itemKey);
    if (learner && !question) return genericItem(answer, "Aufgabenstellung nicht vollständig gespeichert", "Antworttext nicht vollständig gespeichert", "Lösung nicht vollständig gespeichert");
    const options = objects(question?.options);
    const selected = new Set(strings(value.optionIds));
    return genericItem(answer, display(question?.prompt ?? answer.itemKey), options.filter((option) => selected.has(String(option.id))).map((option) => display(option.label ?? option.text)).join(", "), options.filter((option) => strings(question?.correctOptionIds).includes(String(option.id))).map((option) => display(option.label ?? option.text)).join(", "));
  }
  if (exerciseType === "lueckentext") {
    const parts = objects(object(detail.contentPage?.publishedVersion?.exerciseConfig)?.parts);
    const prompt = learner ? parts.length ? parts.map((part) => part.type === "text" ? display(part.text) : part.id === answer.itemKey ? "[Deine Lücke]" : "[…] ").join("") : "Lückentext nicht vollständig gespeichert" : `Lücke ${answer.itemKey}`;
    return genericItem(answer, prompt, display(value.value), display(value.expected));
  }
  if (exerciseType === "laute-erkennen") {
    const response = value.responseState === "omitted" ? "Keine Antwort" : lautGroupLabel(value.spokenSound as LautGroup | "other" | null);
    const item = genericItem(answer, `${display(value.word)} · ${value.position === "initial" ? "Anlaut" : "Auslaut"}`, response, lautGroupLabel(value.expectedSound as LautGroup), [`${display(value.soundCount)} gesprochene Laute`, "Mündliche Antwort, durch Fachkraft dokumentiert"]);
    return value.rating === "uncertain" ? { ...item, status: "uncertain" } : item;
  }
  if (exerciseType === "blitzlesen") {
    const item = genericItem(answer, display(value.word ?? answer.itemKey), display(value.rating), undefined, typeof value.displayDurationMs === "number" ? [`Anzeigedauer: ${value.displayDurationMs} ms`] : []);
    return value.rating === "uncertain" ? { ...item, status: "uncertain" } : item;
  }
  if (exerciseType === "silbentrennung") {
    const surface = display(value.surface ?? answer.itemKey);
    return genericItem(answer, surface, splitWord(surface, numbers(value.splits)), splitWord(surface, numbers(value.expectedSplits)));
  }
  if (exerciseType === "reime") return genericItem(answer, `${display(value.first)} – ${display(value.second)}`, value.answer === true ? "Reimt sich" : "Reimt sich nicht", value.expected === true ? "Reimt sich" : "Reimt sich nicht");
  if (exerciseType === "zeitformen") return genericItem(answer, display(value.sentence), display(value.chosenTense), display(value.expectedTense));
  if (exerciseType === "zeitformen-konjugieren") {
    const pronouns = ["ich", "du", "er/sie/es", "wir", "ihr", "sie/Sie"];
    const chosen = strings(value.values);
    const expected = strings(value.solutions);
    const notes = expected.flatMap((solution, index) => chosen[index] === solution ? [] : [`${pronouns[index]}: ${chosen[index] || "keine Angabe"} (richtig: ${solution})`]);
    return genericItem(answer, `${display(value.infinitive)} · ${display(value.tense)}`, chosen.join(", "), expected.join(", "), notes);
  }
  if (exerciseType === "dehnungs-h" || exerciseType === "kurze-lange-vokale") return genericItem(answer, display(value.expectedSurface ?? answer.itemKey), display(value.chosenSurface), display(value.expectedSurface));
  if (exerciseType === "morphem-stamm") {
    const item = findById(generated.items, answer.itemKey);
    return genericItem(answer, display(item?.targetWord ?? answer.itemKey), `${display(value.selectedGrapheme)} · ${display(value.selectedRoot)}`, `${display(value.expectedGrapheme)} · ${display(value.expectedRoot)}`);
  }
  if (exerciseType === "morphem-zerlegung") {
    const item = findById(generated.items, answer.itemKey);
    const selected = object(value.selectedSegments) ?? {};
    const expected = object(value.expectedSegments) ?? item ?? {};
    const segments = (source: JsonObject) => `${display(source.prefix || "–")} | ${display(source.stem || "–")} | ${display(source.suffix || "–")}`;
    return genericItem(answer, display(value.word ?? item?.word ?? answer.itemKey), segments(selected), segments(expected));
  }
  if (exerciseType === "mathe") return genericItem(answer, `${display(value.left)} ${operationSymbol(value.operation)} ${display(value.right)}`, display(value.value), display(value.expected));
  if (exerciseType === "einmaleins") return genericItem(answer, `${display(value.row)} × ${display(value.factor)}`, value.skipped ? "Übersprungen" : display(value.value), display(value.expected));
  if (exerciseType === "stellenwert-addition") {
    const firstAddend = display(value.firstAddend);
    const secondAddend = display(value.secondAddend);
    const notes = [
      typeof value.transcriptionCorrectionCount === "number" && value.transcriptionCorrectionCount > 0
        ? `Übertragung korrigiert: ${value.transcriptionCorrectionCount} Korrektur${value.transcriptionCorrectionCount === 1 ? "" : "en"}`
        : "Übertragung ohne Korrektur",
    ];
    if (typeof value.firstIncorrectWrittenFirstAddend === "number" && typeof value.firstIncorrectWrittenSecondAddend === "number") {
      notes.push(`Erster fehlerhafter Übertragungsversuch: ${value.firstIncorrectWrittenFirstAddend} + ${value.firstIncorrectWrittenSecondAddend}`);
    }
    if (value.intermediateEntered !== value.expectedIntermediate) notes.push(`Zwischenschritt: ${display(value.intermediateEntered)} (richtig: ${display(value.expectedIntermediate)})`);
    if (value.resultEntered !== value.expectedResult) notes.push(`Endergebnis: ${display(value.resultEntered)} (richtig: ${display(value.expectedResult)})`);
    return genericItem(
      answer,
      `Sachsituation: ${firstAddend} Paletten im Lager, ${secondAddend} Paletten auf dem Lieferwagen`,
      `${display(value.writtenFirstAddend)} + ${display(value.tensPart)} = ${display(value.intermediateEntered)}; ${display(value.intermediateEntered)} + ${display(value.onesPart)} = ${display(value.resultEntered)}`,
      `${firstAddend} + ${display(value.tensPart)} = ${display(value.expectedIntermediate)}; ${display(value.expectedIntermediate)} + ${display(value.onesPart)} = ${display(value.expectedResult)}`,
      notes,
    );
  }
  if (exerciseType === "transkodieren") {
    const number = (entry: unknown) => typeof entry === "number" ? entry.toLocaleString("de-DE") : display(entry);
    return genericItem(answer, display(value.word), number(value.value), number(value.expected), [
      value.responseMode === "choice" ? "Auswahl aus drei Zahlen" : "Freie Zahleneingabe",
      "Gewertet wird ausschließlich der erste Versuch.",
      ...(typeof value.secondValue === "number" ? [`Zweiter Versuch: ${number(value.secondValue)} (${value.secondValue === value.expected ? "richtig" : "falsch; Lösung angezeigt"})`] : []),
    ]);
  }
  if (exerciseType === "runden") {
    const number = (entry: unknown) => typeof entry === "number" ? entry.toLocaleString("de-DE", { maximumFractionDigits: 1 }) : display(entry);
    return genericItem(answer, `${number(value.source)} auf ${display(value.place)}`, number(value.value), number(value.expected));
  }
  if (exerciseType === "zahlen-zerlegen") return zahlenZerlegenItem(answer, value);
  if (exerciseType === "frosch-hupfen") return genericItem(answer, `${display(value.start)} ${display(value.operation)} ${display(value.steps)}`, display(value.value), display(value.expected));
  if (exerciseType === "waage") return genericItem(answer, `${display(value.left)} ↔ ${display(value.right)}`, display(value.answer), display(value.expected));
  if (exerciseType === "mengen-match") return genericItem(answer, `${display(value.emoji)} · dargestellte Menge`, display(value.value), display(value.target));
  if (exerciseType === "zaehlen-tippen") return genericItem(answer, `${display(value.emoji)} · ${display(value.tappedCount)} Objekte angetippt`, display(value.value), display(value.target));
  if (exerciseType === "sortierspiel") {
    const categories = objects(generated.categories);
    const label = (id: unknown) => display(categories.find((category) => category.id === id)?.label ?? id);
    return genericItem(answer, `Objekt ${display(value.emoji ?? answer.itemKey)} zuordnen`, label(value.chosenCategoryId), label(value.expectedCategoryId));
  }
  if (exerciseType === "schriftlich-rechnen") return writtenArithmeticItem(answer, value);
  if (answer.dataOrigin === "legacy_v1") return legacy();
  if (learner) return genericItem(answer, "Aufgabenstellung nicht vollständig gespeichert", "Antwort nicht vollständig darstellbar", "Lösung nicht vollständig gespeichert");
  return genericItem(answer, answer.itemKey, displayStoredValue(value));
}

/** Unlike the professional documentation this never falls back to rendering arbitrary JSON. */
export function documentLearnerAnswers(detail: DocumentableRunDetail): DocumentationItem[] {
  return detail.answers.map((answer) => answerItem(detail.exerciseType, answer, detail, true));
}

export function documentRun(detail: DocumentableRunDetail): RunDocumentation {
  const generated = object(detail.settings);
  const settings = object(generated?.settings) ?? (generated && !generated.seed && !generated.generatorVersion ? generated : {});
  const reading = readingSnapshotSchema.safeParse(generated?.readingProgression);
  const documentedAnswers = detail.exerciseType === "sortierspiel" ? detail.answers : latestAnswers(detail.answers);
  const items = detail.exerciseType === "wortarten"
    ? legacyWordartenItems(documentedAnswers) ?? documentedAnswers.map((answer) => answerItem(detail.exerciseType, answer, detail))
    : documentedAnswers.map((answer) => answerItem(detail.exerciseType, answer, detail));
  return {
    settings: [...(reading.success ? [{ label: "Buchstaben-/Lautprofil", value: reading.data.enabled ? "Aktiv" : "Deaktiviert" }, ...(reading.data.enabled ? [{ label: "Freigegebene Einheiten", value: reading.data.allowedUnits.map((id) => READING_UNITS.find((unit) => unit.id === id)?.label ?? id).join(", ") || "Keine" }, { label: "Einheitenkatalog", value: String(reading.data.catalogVersion) }] : [])] : []), ...Object.entries(settings)
      .filter(([key, value]) => !(detail.exerciseType === "einmaleins" && settings.trainingMode === "core" && key === "rows") && !((key === "syllableCounts" || key === "groupIds") && Array.isArray(value) && value.length === 0))
      .map(([key, value]) => ({ label: key === "wordCount" && typeof settings.materialBankId === "string" ? "Anzahl Einträge" : settingLabels[key] ?? key.replace(/([A-Z])/g, " $1").replace(/^./, (letter) => letter.toLocaleUpperCase("de-DE")), value: detail.exerciseType === "laute-erkennen" && key === "position" ? value === "initial" ? "Anlaut" : "Auslaut" : detail.exerciseType === "laute-erkennen" && key === "soundCounts" && Array.isArray(value) && value.length === 0 ? "Alle verfügbaren Wortlängen" : detail.exerciseType === "transkodieren" && key === "difficulty" ? ({ einfach: "Einfach", normal: "Normal", schwer: "Schwer" }[String(value)] ?? display(value)) : formatSettingValue(key, value) }))],
    items,
    correctCount: items.reduce((total, item) => total + item.correctUnits, 0),
    uncertainCount: items.filter((item) => item.status === "uncertain").length,
    wrongCount: items.reduce((total, item) => total + item.wrongUnits, 0),
  };
}

function localTimestamp(value: string) {
  return new Intl.DateTimeFormat("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(new Date(value));
}

export function runDocumentationMarkdown(meta: RunMarkdownMeta, documentation: RunDocumentation) {
  const lines = [
    `# Übungsverlauf: ${meta.title}`,
    "",
    `- Lernende/r: ${meta.learnerName}`,
    `- Aufgabentyp: ${meta.exerciseType}`,
    `- Gestartet: ${localTimestamp(meta.startedAt)}`,
    `- Dauer: ${meta.durationSeconds === null ? "noch offen" : `${formatDuration(meta.durationSeconds)}${meta.abortReason ? " bis zur letzten Eingabe" : ""}`}`,
    ...(meta.abortReason ? [`- Status: ${meta.abortReason === "INACTIVITY" ? "Automatisch abgebrochen nach 15 Minuten Inaktivität" : "Abgebrochen – Zeitende ohne Antwort"}`] : meta.status ? [`- Status: ${meta.status === "COMPLETED" ? "Abgeschlossen" : meta.status === "STARTED" ? "Gestartet" : meta.status === "ABORTED" ? "Abgebrochen" : "Importiert"}`] : []),
    `- Ergebnis: ${meta.score ?? "–"}/${meta.maxScore ?? "–"} richtig${meta.exerciseType === "blitzlesen" ? `, ${documentation.uncertainCount} unsicher` : ""}, ${meta.wrongAnswerCount} falsch`,
    "",
    "## Einstellungen",
    "",
    ...(documentation.settings.length ? documentation.settings.map((setting) => `${setting.label}: ${setting.value}  `) : ["Keine gespeicherten Einstellungen."]),
    "",
    meta.exerciseType === "stellenwert-addition" ? "## Aufgaben" : meta.exerciseType === "blitzlesen" ? "## Bewertungen" : "## Falsche Antworten",
    "",
  ];
  const documentedItems = meta.exerciseType === "stellenwert-addition"
    ? documentation.items
    : meta.exerciseType === "blitzlesen"
      ? documentation.items
      : documentation.items.filter((item) => item.correct === false || item.notes.length > 0);
  if (!documentedItems.length) lines.push("Keine.");
  for (const item of documentedItems) {
    lines.push(item.prompt);
    const notes = meta.exerciseType === "blitzlesen"
      ? [`Bewertung: ${item.response ?? "ohne Bewertung"}`, ...item.notes]
      : meta.exerciseType === "stellenwert-addition"
        ? [
          item.response ? `Eingegebener Rechenweg: ${item.response}` : "Kein Rechenweg gespeichert",
          ...(item.expected ? [`Richtiger Rechenweg: ${item.expected}`] : []),
          ...item.notes,
          ]
        : item.notes.length ? item.notes : [
          item.response ? `Antwort: ${item.response}` : "Antwort nicht korrekt",
          ...(item.expected ? [`Richtig: ${item.expected}`] : []),
        ];
    lines.push(...notes.map((note) => `- ${note}`), "");
  }
  return lines.join("\n").trimEnd();
}
